import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Circle, CircleCheck, FileOutput, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

type LockChoice = 'diff' | 'new'

/** Lock modes the user can pick when a base already exists. */
export type SalaryLockChoiceMode = 'DIFFERENCE' | 'RECREATE'

interface SalaryLockModalProps {
  onResolve: PromptResolve<{ mode: SalaryLockChoiceMode } | null>
  /** Company-wide "lock all": the choice applies to every already-locked user. */
  allUsers?: boolean
}

/**
 * Shown when locking a month. Lets the user choose between writing the change
 * to a separate difference base (keeps the original, avoids double payment) or
 * rebuilding the base from scratch (permanently deletes existing differences
 * for the month). With no base yet, "write difference" simply creates the base.
 */
export const SalaryLockModal: React.FC<SalaryLockModalProps> = ({
  onResolve,
  allUsers = false,
}) => {
  const translations = useTranslate({
    title: 'salary.lock',
    titleAll: 'salary.lock-all',
    desc: 'salary.lock-modal-desc',
    descAll: 'salary.lock-all-modal-desc',
    optionDiff: 'salary.lock-option-diff',
    optionDiffDesc: 'salary.lock-option-diff-desc',
    optionNew: 'salary.lock-option-new',
    optionNewDesc: 'salary.lock-option-new-desc',
    optionNewWarning: 'salary.lock-option-new-warning',
    submitDiff: 'salary.lock-submit-diff',
    submitNew: 'salary.lock-submit-new',
    cancel: 'common.cancel',
  })

  const [choice, setChoice] = useState<LockChoice>('diff')
  const isNew = choice === 'new'
  const isDiff = choice === 'diff'

  return (
    <Dialog open onOpenChange={open => !open && onResolve(null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {allUsers ? translations.titleAll : translations.title}
          </DialogTitle>
          <DialogDescription>
            {allUsers ? translations.descAll : translations.desc}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setChoice('diff')}
            className={cn(
              'flex flex-col gap-1.5 rounded-lg border p-4 text-left transition-colors',
              isDiff
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            )}
          >
            <div className="flex items-center gap-2.5">
              {isDiff ? (
                <CircleCheck className="size-[18px] text-blue-600" />
              ) : (
                <Circle className="size-[18px] text-gray-300" />
              )}
              <span className="font-semibold text-gray-900">
                {translations.optionDiff}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {translations.optionDiffDesc}
            </p>
          </button>

          <button
            type="button"
            onClick={() => setChoice('new')}
            className={cn(
              'flex flex-col gap-1.5 rounded-lg border p-4 text-left transition-colors',
              isNew
                ? 'border-red-500 bg-red-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            )}
          >
            <div className="flex items-center gap-2.5">
              {isNew ? (
                <CircleCheck className="size-[18px] text-red-600" />
              ) : (
                <Circle className="size-[18px] text-gray-300" />
              )}
              <span className="font-semibold text-gray-900">
                {translations.optionNew}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {translations.optionNewDesc}
            </p>
            <p className="text-sm font-medium text-red-600">
              {translations.optionNewWarning}
            </p>
          </button>
        </div>

        <DialogFooter>
          <Button variant="outline" size="lg" onClick={() => onResolve(null)}>
            {translations.cancel}
          </Button>
          <Button
            size="lg"
            variant={isNew ? 'destructive' : 'default'}
            onClick={() =>
              onResolve({ mode: isNew ? 'RECREATE' : 'DIFFERENCE' })
            }
          >
            {isNew ? <TriangleAlert /> : <FileOutput />}
            {isNew ? translations.submitNew : translations.submitDiff}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
